// ═══════════════════════════════════════════════════════════════
// DEBRIS — Force Account Tracking
// City employee labor hours and equipment usage for FEMA reimbursement
// Uses debrisForceAccountLabor + debrisForceAccountEquipment tables
// Integrates with ERC roster data from PersonnelRoster for auto-fill
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, Modal, Stack, SimpleGrid, Tabs, Checkbox, Alert,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconRefresh, IconDownload, IconUpload, IconClock,
  IconTruck, IconTrash, IconUsers, IconCalendar, IconCheck,
  IconAlertTriangle, IconSearch, IconPencil, IconPaperclip,
  IconExternalLink,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { FEMA_EQUIP_RATES } from '../../data/femaEquipmentRates';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { fetchRecords, queryRecords, createRecord, updateRecord, deleteRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import type { PersonnelRecord } from './PersonnelRoster';
import { useDebrisStore } from '../../store/debris-store';
import type { FAClaim, ClaimStatus } from '../../types/checklist';

// ── FA document link marker ──
const FA_LINK_RE = /\|\|\|FALINK\|\|\|([\s\S]*?)\|\|\|/;

// ── Differential pay marker ──
const DIFF_RE = /\|\|\|DIFF\|\|\|([\s\S]*?)\|\|\|/;

interface DiffData { night: number; weekend: number; holiday: number; }

function parseDiff(notes: string): DiffData {
  const m = notes.match(DIFF_RE);
  if (!m) return { night: 0, weekend: 0, holiday: 0 };
  try { return { night: 0, weekend: 0, holiday: 0, ...JSON.parse(m[1]) }; } catch { return { night: 0, weekend: 0, holiday: 0 }; }
}

function stripDiffMarker(notes: string): string {
  return notes.replace(DIFF_RE, '').trim();
}

function buildDiffMarker(diff: DiffData): string {
  if (!diff.night && !diff.weekend && !diff.holiday) return '';
  return ` |||DIFF|||${JSON.stringify(diff)}|||`;
}

function parseFALink(notes: string): { t: string; id: number } | null {
  const m = notes.match(FA_LINK_RE);
  if (!m) return null;
  try { return JSON.parse(m[1]); } catch { return null; }
}

function makeFALinkMarker(type: 'labor' | 'equipment', id: number): string {
  return `|||FALINK|||${JSON.stringify({ t: type, id })}|||`;
}

const FL = FIELDS.debrisForceAccountLabor;
const FE = FIELDS.debrisForceAccountEquipment;
const FDoc = FIELDS.debrisDocuments;
const FC = FIELDS.debrisForceAccountClaims;

// ── Types ──

interface LaborRow {
  id: number;
  incidentId: string;
  employeeName: string;
  employeeId: string;
  department: string;
  classification: string;
  dateWorked: string;
  hoursRegular: number;
  hoursOvertime: number;
  hourlyRate: number;
  totalCost: number;
  equipmentUsed: string;
  ercDistrict: string;
  notes: string;
  disasterNumber: string;
  femaCategory: string;
  nightDiff: number;
  weekendDiff: number;
  holidayDiff: number;
  diffTotal: number;
}

interface EquipmentRow {
  id: number;
  incidentId: string;
  fleetNumber: string;
  equipmentType: string;
  description: string;
  dateUsed: string;
  hoursUsed: number;
  hourlyRate: number;
  totalCost: number;
  operatorName: string;
  ercDistrict: string;
  notes: string;
  disasterNumber: string;
  femaCategory: string;
}

function mapLabor(r: Record<string, unknown>): LaborRow {
  const reg = Number(r[FL.hoursRegular]) || 0;
  const ot = Number(r[FL.hoursOvertime]) || 0;
  const rate = Number(r[FL.hourlyRate]) || 0;
  const rawNotes = String(r[FL.notes] || '');
  const diff = parseDiff(rawNotes);
  const totalHours = reg + ot;
  const diffTotal = totalHours * (diff.night + diff.weekend + diff.holiday);
  const baseCost = (reg * rate) + (ot * rate * 1.5);
  return {
    id: r.record_id as number,
    incidentId: String(r[FL.incidentId] || ''),
    employeeName: String(r[FL.employeeName] || ''),
    employeeId: String(r[FL.employeeId] || ''),
    department: String(r[FL.department] || ''),
    classification: String(r[FL.classification] || ''),
    dateWorked: String(r[FL.dateWorked] || ''),
    hoursRegular: reg,
    hoursOvertime: ot,
    hourlyRate: rate,
    totalCost: Number(r[FL.totalCost]) || (baseCost + diffTotal),
    equipmentUsed: String(r[FL.equipmentUsed] || ''),
    ercDistrict: String(r[FL.ercDistrict] || ''),
    notes: stripDiffMarker(rawNotes),
    disasterNumber: String(r[FL.disasterNumber] || ''),
    femaCategory:   String(r[FL.femaCategory] || ''),
    nightDiff: diff.night,
    weekendDiff: diff.weekend,
    holidayDiff: diff.holiday,
    diffTotal,
  };
}

function mapEquipment(r: Record<string, unknown>): EquipmentRow {
  const hrs = Number(r[FE.hoursUsed]) || 0;
  const rate = Number(r[FE.hourlyRate]) || 0;
  return {
    id: r.record_id as number,
    incidentId: String(r[FE.incidentId] || ''),
    fleetNumber: String(r[FE.fleetNumber] || ''),
    equipmentType: String(r[FE.equipmentType] || ''),
    description: String(r[FE.description] || ''),
    dateUsed: String(r[FE.dateUsed] || ''),
    hoursUsed: hrs,
    hourlyRate: rate,
    totalCost: Number(r[FE.totalCost]) || (hrs * rate),
    operatorName: String(r[FE.operatorName] || ''),
    ercDistrict: String(r[FE.ercDistrict] || ''),
    notes: String(r[FE.notes] || ''),
    disasterNumber: String(r[FE.disasterNumber] || ''),
    femaCategory:   String(r[FE.femaCategory] || ''),
  };
}

function mapClaim(r: Record<string, unknown>): FAClaim {
  let entryIds: number[] = [];
  try { entryIds = JSON.parse(String(r[FC.entryIds] || '[]')); } catch { entryIds = []; }
  return {
    id: r.record_id as number,
    claimNumber: String(r[FC.claimNumber] || ''),
    claimType: (String(r[FC.claimType] || 'labor')) as 'labor' | 'equipment',
    incidentId: String(r[FC.incidentId] || ''),
    billingPeriodStart: String(r[FC.billingPeriodStart] || ''),
    billingPeriodEnd: String(r[FC.billingPeriodEnd] || ''),
    status: (String(r[FC.status] || 'draft')) as ClaimStatus,
    totalCost: Number(r[FC.totalCost]) || 0,
    entryIds,
    notes: String(r[FC.notes] || ''),
  };
}

function fmtCurrency(val: number): string {
  return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(val: string): string {
  if (!val) return '—';
  try {
    const d = new Date(val);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return val; }
}

const ERC_OPTIONS = [
  { value: 'ERC-1', label: 'ERC-1' },
  { value: 'ERC-2', label: 'ERC-2' },
  { value: 'ERC-3', label: 'ERC-3' },
];

// Build searchable options from FEMA 2025 equipment rate schedule
const FEMA_EQUIP_OPTIONS = FEMA_EQUIP_RATES.map(r => ({
  value: r.costCode,
  label: `${r.costCode} — ${r.equipment}${r.manufacturer ? ` (${r.manufacturer})` : ''}${r.specification ? ` · ${r.specification}` : ''} — $${r.rate.toFixed(2)}/${r.unit}`,
}));

// Map costCode → rate entry for auto-fill
const FEMA_EQUIP_BY_CODE = Object.fromEntries(FEMA_EQUIP_RATES.map(r => [r.costCode, r]));

// ── Debris-relevant roles from roster ──

const DEBRIS_RELEVANT_SECTIONS = [
  'operations section',
  'commanders',
];

const DEBRIS_RELEVANT_ROLES = [
  'push crew', 'operator', 'crew lead', 'push crew lead',
  'safety officer', 'safety observer',
  'commander', 'deputy commander', 'ops chief',
  'driver', 'loader operator', 'equipment operator',
];

function isDebrisRelevant(person: PersonnelRecord): boolean {
  const roleLower = (person.role || '').toLowerCase();
  // Check if role matches debris-relevant keywords
  if (DEBRIS_RELEVANT_ROLES.some(r => roleLower.includes(r))) return true;
  // Include anyone with operator or push crew lead certs
  if (person.certs?.operator || person.certs?.pushCrewLead) return true;
  // Include anyone with chainsaw cert (they're on debris crews)
  if (person.certs?.chainsaw4hr || person.certs?.chainsaw8hr) return true;
  return false;
}

function buildClassification(person: PersonnelRecord): string {
  const parts: string[] = [];
  if (person.role) parts.push(person.role);
  if (person.certs?.pushCrewLead && !person.role.toLowerCase().includes('crew lead')) parts.push('Push Crew Lead');
  if (person.certs?.operator && !person.role.toLowerCase().includes('operator')) parts.push('Operator');
  if (person.certs?.safetyObserver && !person.role.toLowerCase().includes('safety')) parts.push('Safety Observer');
  if ((person.certs?.chainsaw4hr || person.certs?.chainsaw8hr) && !person.role.toLowerCase().includes('chainsaw')) parts.push('Chainsaw');
  return parts.join(' / ');
}

// ── CSV Export ──

function exportLaborCSV(data: LaborRow[]) {
  const headers = ['Employee Name', 'Employee ID', 'Department', 'Classification', 'Date', 'Regular Hours', 'OT Hours', 'Hourly Rate', 'Night Diff ($/hr)', 'Weekend Diff ($/hr)', 'Holiday Diff ($/hr)', 'Differential Total', 'Total Cost', 'Equipment', 'ERC'];
  const rows = data.map(r => [
    r.employeeName, r.employeeId, r.department, r.classification, r.dateWorked,
    r.hoursRegular, r.hoursOvertime, r.hourlyRate,
    r.nightDiff || '', r.weekendDiff || '', r.holidayDiff || '', r.diffTotal || '',
    r.totalCost, r.equipmentUsed, r.ercDistrict,
  ]);
  const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'FEMA_Force_Account_Labor_Summary.csv'; a.click();
  URL.revokeObjectURL(url);
}

function exportEquipmentCSV(data: EquipmentRow[]) {
  const headers = ['Fleet #', 'Equipment Type', 'Description', 'Date', 'Hours Used', 'Hourly Rate', 'Total Cost', 'Operator', 'ERC'];
  const rows = data.map(r => [
    r.fleetNumber, r.equipmentType, r.description, r.dateUsed,
    r.hoursUsed, r.hourlyRate, r.totalCost, r.operatorName, r.ercDistrict,
  ]);
  const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'FEMA_Force_Account_Equipment_Summary.csv'; a.click();
  URL.revokeObjectURL(url);
}

// ── Date helpers ──

function dateRange(start: string, end: string): string[] {
  const dates: string[] = [];
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || s > e) return dates;
  const cur = new Date(s);
  while (cur <= e) {
    dates.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

// ── Component ──

export default function ForceAccount() {
  const { activeIncident, user } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  // Document store — for FA source doc attachment
  const { documents, loadDocuments, createDocument, uploadDocumentFile, getDocumentDownloadUrl } = useDebrisStore();

  const [tab, setTab] = useState('labor');
  const [laborData, setLaborData] = useState<LaborRow[]>([]);
  const [equipData, setEquipData] = useState<EquipmentRow[]>([]);
  const [claimsData, setClaimsData] = useState<FAClaim[]>([]);
  const [loading, setLoading] = useState(false);

  // Claims sort
  const [claimSortField, setClaimSortField] = useState('');
  const [claimSortDir, setClaimSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleClaimSort = (field: string) => {
    if (claimSortField === field) setClaimSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setClaimSortField(field); setClaimSortDir('asc'); }
  };

  // New claim modal
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [claimSaving, setClaimSaving] = useState(false);
  const [claimForm, setClaimForm] = useState({
    claimType: 'labor' as 'labor' | 'equipment',
    billingPeriodStart: '',
    billingPeriodEnd: '',
    notes: '',
  });

  // Claim detail modal
  const [claimDetailTarget, setClaimDetailTarget] = useState<FAClaim | null>(null);
  const [claimDetailNotes, setClaimDetailNotes] = useState('');
  const [claimDetailStatus, setClaimDetailStatus] = useState<ClaimStatus>('draft');
  const [claimDetailSaving, setClaimDetailSaving] = useState(false);

  // FA doc modal state
  const [faDocTarget, setFaDocTarget] = useState<{ type: 'labor' | 'equipment'; id: number; label: string } | null>(null);
  const [docUploading, setDocUploading] = useState(false);
  const faFileRef = useRef<HTMLInputElement>(null);

  // Roster data (loaded from debrisDocuments)
  const [rosterPersonnel, setRosterPersonnel] = useState<PersonnelRecord[]>([]);
  const [rosterLoaded, setRosterLoaded] = useState(false);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'labor' | 'equipment'>('labor');
  const [saving, setSaving] = useState(false);

  // Bulk Generate modal
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkDateStart, setBulkDateStart] = useState('');
  const [bulkDateEnd, setBulkDateEnd] = useState('');
  const [bulkHoursReg, setBulkHoursReg] = useState('12');
  const [bulkHoursOT, setBulkHoursOT] = useState('0');
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [bulkFilter, setBulkFilter] = useState('');
  const [bulkShiftFilter, setBulkShiftFilter] = useState('');
  const [bulkRoleFilter, setBulkRoleFilter] = useState('');

  // Roster search in labor modal
  const [rosterSearch, setRosterSearch] = useState('');
  const [showRosterDropdown, setShowRosterDropdown] = useState(false);
  const rosterDropdownRef = useRef<HTMLDivElement>(null);

  // Labor form
  const [laborForm, setLaborForm] = useState({
    employeeName: '', employeeId: '', department: '', classification: '',
    dateWorked: '', hoursRegular: '', hoursOvertime: '', hourlyRate: '',
    equipmentUsed: '', ercDistrict: '', notes: '',
    nightDiff: '', weekendDiff: '', holidayDiff: '',
  });

  // Equipment form
  const [equipForm, setEquipForm] = useState({
    fleetNumber: '', equipmentType: '', description: '',
    dateUsed: '', hoursUsed: '', hourlyRate: '',
    operatorName: '', ercDistrict: '', notes: '',
  });
  const [equipFemaCostCode, setEquipFemaCostCode] = useState<string | null>(null);
  const [editEquipTarget, setEditEquipTarget] = useState<EquipmentRow | null>(null);
  const [editLaborTarget, setEditLaborTarget] = useState<LaborRow | null>(null);

  // Filters
  const [drFilter, setDrFilter] = useState<string | null>(null);

  // Sort
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // ── Load roster from debrisDocuments ──

  const loadRoster = useCallback(async () => {
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.debrisDocuments, { limit: 50 });
      const doc = res.records.find(r => r[FDoc.documentType] === 'roster_data');
      if (doc) {
        const notesRaw = String(doc[FDoc.notes] || '');
        try {
          const data = JSON.parse(notesRaw) as PersonnelRecord[];
          setRosterPersonnel(data);
        } catch { /* bad JSON */ }
      }
    } catch (e) {
      console.error('[ForceAccount] loadRoster', e);
    }
    setRosterLoaded(true);
  }, []);

  const loadData = useCallback(async () => {
    if (!incidentId) { setLaborData([]); setEquipData([]); setClaimsData([]); return; }
    setLoading(true);
    try {
      const incFilter = (field: string) => [{ field, operator: 'eq' as const, value: incidentId }];
      const [laborRes, equipRes, claimsRes] = await Promise.all([
        queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountLabor, { limit: 500, filters: incFilter(FL.incidentId) }),
        queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountEquipment, { limit: 500, filters: incFilter(FE.incidentId) }),
        queryRecords<Record<string, unknown>>(TABLES.debrisForceAccountClaims, { limit: 500, filters: incFilter(FIELDS.debrisForceAccountClaims.incidentId) }),
      ]);
      // Client-side guard: GAB filter may return unscoped records
      setLaborData(laborRes.records.map(mapLabor).filter(r => String(r.incidentId) === incidentId));
      setEquipData(equipRes.records.map(mapEquipment).filter(r => String(r.incidentId) === incidentId));
      setClaimsData(claimsRes.records.map(mapClaim).filter(r => String((r as any).incidentId) === incidentId));
    } catch (e) {
      console.error('[ForceAccount] loadData', e);
    }
    setLoading(false);
  }, [incidentId]);

  useEffect(() => { loadData(); loadRoster(); loadDocuments(); }, [loadData, loadRoster]);

  // ── FA doc helpers ──

  // Map from "labor:123" or "equipment:456" → attached debrisDocument records
  const faDocMap = useMemo(() => {
    const m: Record<string, typeof documents> = {};
    for (const doc of documents) {
      const link = parseFALink(doc.notes || '');
      if (!link) continue;
      const key = `${link.t}:${link.id}`;
      if (!m[key]) m[key] = [];
      m[key].push(doc);
    }
    return m;
  }, [documents]);

  const getFADocs = (type: 'labor' | 'equipment', id: number) => faDocMap[`${type}:${id}`] ?? [];

  const handleFADocUpload = async (file: File) => {
    if (!faDocTarget) return;
    setDocUploading(true);
    try {
      const linkMarker = makeFALinkMarker(faDocTarget.type, faDocTarget.id);
      const newId = await createDocument({
        title: file.name,
        documentType: 'fa_support',
        incidentId,
        uploadedBy: (user as any)?.email || (user as any)?.name || '',
        notes: linkMarker,
      });
      if (!newId) throw new Error('Failed to create document record');
      await uploadDocumentFile(newId, file);
      notifications.show({ message: `${file.name} attached`, color: 'var(--color-success)' });
    } catch (e) {
      notifications.show({ message: e instanceof Error ? e.message : 'Upload failed', color: 'var(--color-error)' });
    }
    setDocUploading(false);
  };

  const handleFADocOpen = async (doc: any) => {
    try {
      const notesRaw: string = doc.notes || '';
      const docIdMatch = notesRaw.match(/\|\|\|DOCID\|\|\|([^|]+)\|\|\|/);
      if (!docIdMatch) {
        notifications.show({ message: 'No file attached to this document', color: '#C68700' });
        return;
      }
      const url = await getDocumentDownloadUrl(docIdMatch[1]);
      window.open(url, '_blank');
    } catch {
      notifications.show({ message: 'Could not open file', color: 'var(--color-error)' });
    }
  };

  // Close roster dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rosterDropdownRef.current && !rosterDropdownRef.current.contains(e.target as Node)) {
        setShowRosterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debris-relevant personnel from roster
  const debrisPersonnel = useMemo(() => {
    // Show all roster personnel — user can filter by role in bulk generate
    // but for individual add, show everyone so they can pick non-debris people too
    return rosterPersonnel;
  }, [rosterPersonnel]);

  // Filtered roster for search dropdown in Add Entry modal
  const filteredRoster = useMemo(() => {
    if (!rosterSearch.trim()) return [];
    const q = rosterSearch.toLowerCase();
    return debrisPersonnel
      .filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.employeeId.includes(q) ||
        (p.role || '').toLowerCase().includes(q)
      )
      .slice(0, 15);
  }, [debrisPersonnel, rosterSearch]);

  // Debris-only personnel for bulk generate
  const bulkEligiblePersonnel = useMemo(() => {
    let list = rosterPersonnel.filter(isDebrisRelevant);
    if (bulkFilter) {
      const q = bulkFilter.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || p.employeeId.includes(q));
    }
    if (bulkShiftFilter) list = list.filter(p => p.shift === bulkShiftFilter);
    if (bulkRoleFilter) list = list.filter(p => (p.role || '').toLowerCase().includes(bulkRoleFilter.toLowerCase()));
    return list;
  }, [rosterPersonnel, bulkFilter, bulkShiftFilter, bulkRoleFilter]);

  // Unique roles for bulk filter
  const bulkRoles = useMemo(() => {
    const s = new Set(rosterPersonnel.filter(isDebrisRelevant).map(p => p.role).filter(Boolean));
    return Array.from(s).sort();
  }, [rosterPersonnel]);

  // Stats
  const laborStats = useMemo(() => ({
    count: laborData.length,
    totalHours: laborData.reduce((s, r) => s + r.hoursRegular + r.hoursOvertime, 0),
    totalCost: laborData.reduce((s, r) => s + r.totalCost, 0),
  }), [laborData]);

  const equipStats = useMemo(() => ({
    count: equipData.length,
    totalHours: equipData.reduce((s, r) => s + r.hoursUsed, 0),
    totalCost: equipData.reduce((s, r) => s + r.totalCost, 0),
  }), [equipData]);

  const currentStats = tab === 'labor' ? laborStats : equipStats;

  // DR numbers present in the data (for filter dropdown)
  const drOptions = useMemo(() => {
    const nums = new Set<string>();
    laborData.forEach(r => { if (r.disasterNumber) nums.add(r.disasterNumber); });
    equipData.forEach(r => { if (r.disasterNumber) nums.add(r.disasterNumber); });
    return Array.from(nums).sort().map(v => ({ value: v, label: v }));
  }, [laborData, equipData]);

  // Sorted + filtered data
  const sortedLabor = useMemo(() => {
    const filtered = drFilter ? laborData.filter(r => r.disasterNumber === drFilter) : laborData;
    return [...filtered].sort((a, b) => {
      if (!sortField) return 0;
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  }, [laborData, sortField, sortDir, drFilter]);

  const sortedEquip = useMemo(() => {
    const filtered = drFilter ? equipData.filter(r => r.disasterNumber === drFilter) : equipData;
    return [...filtered].sort((a, b) => {
      if (!sortField) return 0;
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return sortDir === 'asc' ? n : -n;
    });
  }, [equipData, sortField, sortDir, drFilter]);

  const sortedClaims = useMemo(() => {
    return [...claimsData].sort((a, b) => {
      if (!claimSortField) return 0;
      const va = (a as any)[claimSortField] ?? '';
      const vb = (b as any)[claimSortField] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return claimSortDir === 'asc' ? va - vb : vb - va;
      const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      return claimSortDir === 'asc' ? n : -n;
    });
  }, [claimsData, claimSortField, claimSortDir]);

  // ── Handlers ──

  const selectRosterPerson = (person: PersonnelRecord) => {
    setLaborForm(f => ({
      ...f,
      employeeName: person.name,
      employeeId: person.employeeId,
      department: person.erc,
      classification: buildClassification(person),
      equipmentUsed: person.fleetNumber || '',
      ercDistrict: person.erc,
    }));
    setRosterSearch('');
    setShowRosterDropdown(false);
  };

  const openLaborAdd = () => {
    setModalType('labor');
    setEditLaborTarget(null);
    setLaborForm({
      employeeName: '', employeeId: '', department: '', classification: '',
      dateWorked: '', hoursRegular: '', hoursOvertime: '', hourlyRate: '',
      equipmentUsed: '', ercDistrict: '', notes: '',
      nightDiff: '', weekendDiff: '', holidayDiff: '',
    });
    setRosterSearch('');
    setShowRosterDropdown(false);
    setModalOpen(true);
  };

  const openLaborEdit = (r: LaborRow) => {
    setModalType('labor');
    setEditLaborTarget(r);
    setLaborForm({
      employeeName: r.employeeName,
      employeeId: r.employeeId,
      department: r.department,
      classification: r.classification,
      dateWorked: r.dateWorked ? r.dateWorked.slice(0, 10) : '',
      hoursRegular: r.hoursRegular ? String(r.hoursRegular) : '',
      hoursOvertime: r.hoursOvertime ? String(r.hoursOvertime) : '',
      hourlyRate: r.hourlyRate ? String(r.hourlyRate) : '',
      equipmentUsed: r.equipmentUsed,
      ercDistrict: r.ercDistrict,
      notes: r.notes,
      nightDiff: r.nightDiff ? String(r.nightDiff) : '',
      weekendDiff: r.weekendDiff ? String(r.weekendDiff) : '',
      holidayDiff: r.holidayDiff ? String(r.holidayDiff) : '',
    });
    setRosterSearch('');
    setShowRosterDropdown(false);
    setModalOpen(true);
  };

  const openEquipAdd = () => {
    setModalType('equipment');
    setEditEquipTarget(null);
    setEquipForm({
      fleetNumber: '', equipmentType: '', description: '',
      dateUsed: '', hoursUsed: '', hourlyRate: '',
      operatorName: '', ercDistrict: '', notes: '',
    });
    setEquipFemaCostCode(null);
    setModalOpen(true);
  };

  const openEquipEdit = (r: EquipmentRow) => {
    setModalType('equipment');
    setEditEquipTarget(r);
    setEquipForm({
      fleetNumber: r.fleetNumber,
      equipmentType: r.equipmentType,
      description: r.description,
      dateUsed: r.dateUsed ? r.dateUsed.slice(0, 10) : '',
      hoursUsed: r.hoursUsed ? String(r.hoursUsed) : '',
      hourlyRate: r.hourlyRate ? String(r.hourlyRate) : '',
      operatorName: r.operatorName,
      ercDistrict: r.ercDistrict,
      notes: r.notes,
    });
    setEquipFemaCostCode(null);
    setModalOpen(true);
  };

  const openBulkGenerate = () => {
    setBulkDateStart('');
    setBulkDateEnd('');
    setBulkHoursReg('12');
    setBulkHoursOT('0');
    setBulkSelectedIds(new Set());
    setBulkFilter('');
    setBulkShiftFilter('');
    setBulkRoleFilter('');
    setBulkOpen(true);
  };

  const toggleBulkPerson = (uniqueKey: string) => {
    setBulkSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(uniqueKey)) next.delete(uniqueKey);
      else next.add(uniqueKey);
      return next;
    });
  };

  const selectAllBulk = () => {
    const keys = bulkEligiblePersonnel.map(p => `${p.employeeId || p.name}-${p.shift}`);
    setBulkSelectedIds(new Set(keys));
  };

  const deselectAllBulk = () => {
    setBulkSelectedIds(new Set());
  };

  const bulkPreviewCount = useMemo(() => {
    const dates = dateRange(bulkDateStart, bulkDateEnd);
    return bulkSelectedIds.size * dates.length;
  }, [bulkSelectedIds, bulkDateStart, bulkDateEnd]);

  const saveLabor = async () => {
    if (!laborForm.employeeName.trim()) {
      notifications.show({ title: 'Error', message: 'Employee name is required', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      const reg = parseFloat(laborForm.hoursRegular) || 0;
      const ot = parseFloat(laborForm.hoursOvertime) || 0;
      const rate = parseFloat(laborForm.hourlyRate) || 0;
      const diff: DiffData = {
        night: parseFloat(laborForm.nightDiff) || 0,
        weekend: parseFloat(laborForm.weekendDiff) || 0,
        holiday: parseFloat(laborForm.holidayDiff) || 0,
      };
      const diffPerHour = diff.night + diff.weekend + diff.holiday;
      const total = (reg * rate) + (ot * rate * 1.5) + ((reg + ot) * diffPerHour);
      const notesWithDiff = laborForm.notes + buildDiffMarker(diff);

      const payload: Record<string, unknown> = {
        [FL.employeeName]: laborForm.employeeName,
        [FL.employeeId]: laborForm.employeeId,
        [FL.department]: laborForm.department,
        [FL.classification]: laborForm.classification,
        [FL.dateWorked]: laborForm.dateWorked,
        [FL.hoursRegular]: reg,
        [FL.hoursOvertime]: ot,
        [FL.hourlyRate]: rate,
        [FL.totalCost]: total,
        [FL.equipmentUsed]: laborForm.equipmentUsed,
        [FL.ercDistrict]: laborForm.ercDistrict,
        [FL.notes]: notesWithDiff,
      };
      if (incidentId) payload[FL.incidentId] = incidentId;

      if (editLaborTarget) {
        await updateRecord(TABLES.debrisForceAccountLabor, editLaborTarget.id, payload);
        notifications.show({ title: 'Updated', message: 'Labor entry updated', color: 'green' });
      } else {
        await createRecord(TABLES.debrisForceAccountLabor, payload);
        notifications.show({ title: 'Added', message: 'Labor entry saved', color: 'green' });
      }
      setEditLaborTarget(null);
      setModalOpen(false);
      await loadData();
    } catch (e) {
      notifications.show({ title: 'Error', message: String(e), color: 'red' });
    }
    setSaving(false);
  };

  const saveBulkGenerate = async () => {
    const dates = dateRange(bulkDateStart, bulkDateEnd);
    if (dates.length === 0) {
      notifications.show({ title: 'Error', message: 'Invalid date range', color: 'red' });
      return;
    }
    if (bulkSelectedIds.size === 0) {
      notifications.show({ title: 'Error', message: 'No personnel selected', color: 'red' });
      return;
    }

    setBulkSaving(true);
    try {
      const selectedPeople = bulkEligiblePersonnel.filter(
        p => bulkSelectedIds.has(`${p.employeeId || p.name}-${p.shift}`)
      );

      const reg = parseFloat(bulkHoursReg) || 0;
      const ot = parseFloat(bulkHoursOT) || 0;

      let created = 0;
      let errors = 0;

      // Create records in batches of 10 to avoid overwhelming the API
      const allPayloads: Record<string, unknown>[] = [];
      for (const person of selectedPeople) {
        const rate = person.hourlyRate || 0;
        const total = (reg * rate) + (ot * rate * 1.5);
        for (const date of dates) {
          const payload: Record<string, unknown> = {
            [FL.employeeName]: person.name,
            [FL.employeeId]: person.employeeId,
            [FL.department]: person.erc,
            [FL.classification]: buildClassification(person),
            [FL.dateWorked]: date,
            [FL.hoursRegular]: reg,
            [FL.hoursOvertime]: ot,
            [FL.hourlyRate]: rate,
            [FL.totalCost]: total,
            [FL.equipmentUsed]: person.fleetNumber || '',
            [FL.ercDistrict]: person.erc,
            [FL.notes]: `Bulk generated - ${person.shift}-Shift`,
          };
          if (incidentId) payload[FL.incidentId] = incidentId;
          allPayloads.push(payload);
        }
      }

      // Process in batches
      const BATCH_SIZE = 10;
      for (let i = 0; i < allPayloads.length; i += BATCH_SIZE) {
        const batch = allPayloads.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(
          batch.map(p => createRecord(TABLES.debrisForceAccountLabor, p))
        );
        results.forEach(r => {
          if (r.status === 'fulfilled') created++;
          else errors++;
        });
      }

      notifications.show({
        title: 'Bulk Generate Complete',
        message: `Created ${created} labor records${errors > 0 ? ` (${errors} errors)` : ''}`,
        color: errors > 0 ? 'orange' : 'green',
      });
      setBulkOpen(false);
      await loadData();
    } catch (e) {
      notifications.show({ title: 'Error', message: String(e), color: 'red' });
    }
    setBulkSaving(false);
  };

  const saveEquipment = async () => {
    if (!equipForm.fleetNumber.trim() && !equipForm.description.trim()) {
      notifications.show({ title: 'Error', message: 'Fleet # or description required', color: 'red' });
      return;
    }
    if (equipForm.description.trim().length > 0 && equipForm.description.trim().length < 6) {
      notifications.show({ title: 'Error', message: 'Description must be at least 6 characters', color: 'red' });
      return;
    }
    setSaving(true);
    try {
      const hrs = parseFloat(equipForm.hoursUsed) || 0;
      const rate = parseFloat(equipForm.hourlyRate) || 0;
      const total = hrs * rate;

      const payload: Record<string, unknown> = {
        [FE.fleetNumber]: equipForm.fleetNumber,
        [FE.equipmentType]: equipForm.equipmentType,
        [FE.description]: equipForm.description,
        [FE.dateUsed]: equipForm.dateUsed,
        [FE.hoursUsed]: hrs,
        [FE.hourlyRate]: rate,
        [FE.totalCost]: total,
        [FE.operatorName]: equipForm.operatorName,
        [FE.ercDistrict]: equipForm.ercDistrict,
        [FE.notes]: equipForm.notes,
      };
      if (incidentId) payload[FE.incidentId] = incidentId;

      if (editEquipTarget) {
        await updateRecord(TABLES.debrisForceAccountEquipment, editEquipTarget.id, payload);
        notifications.show({ title: 'Updated', message: 'Equipment entry updated', color: 'green' });
      } else {
        await createRecord(TABLES.debrisForceAccountEquipment, payload);
        notifications.show({ title: 'Added', message: 'Equipment entry saved', color: 'green' });
      }
      setEditEquipTarget(null);
      setModalOpen(false);
      await loadData();
    } catch (e) {
      notifications.show({ title: 'Error', message: String(e), color: 'red' });
    }
    setSaving(false);
  };

  const deleteLabor = async (id: number) => {
    if (!confirm('Delete this labor entry?')) return;
    try {
      await deleteRecord(TABLES.debrisForceAccountLabor, id);
      await loadData();
      notifications.show({ title: 'Deleted', message: 'Labor entry removed', color: 'green' });
    } catch (e) {
      notifications.show({ title: 'Error', message: String(e), color: 'red' });
    }
  };

  const deleteEquip = async (id: number) => {
    if (!confirm('Delete this equipment entry?')) return;
    try {
      await deleteRecord(TABLES.debrisForceAccountEquipment, id);
      await loadData();
      notifications.show({ title: 'Deleted', message: 'Equipment entry removed', color: 'green' });
    } catch (e) {
      notifications.show({ title: 'Error', message: String(e), color: 'red' });
    }
  };

  // ── Claims handlers ──

  const openNewClaim = () => {
    setClaimForm({ claimType: 'labor', billingPeriodStart: '', billingPeriodEnd: '', notes: '' });
    setClaimModalOpen(true);
  };

  const saveNewClaim = async () => {
    if (!claimForm.billingPeriodStart || !claimForm.billingPeriodEnd) {
      notifications.show({ title: 'Error', message: 'Billing period start and end are required', color: 'red' });
      return;
    }
    const start = claimForm.billingPeriodStart;
    const end = claimForm.billingPeriodEnd;
    if (start > end) {
      notifications.show({ title: 'Error', message: 'Start date must be before end date', color: 'red' });
      return;
    }

    setClaimSaving(true);
    try {
      // Filter matching entries by type and date range
      let matchingIds: number[] = [];
      let totalCost = 0;

      if (claimForm.claimType === 'labor') {
        const matching = laborData.filter(r => {
          const d = (r.dateWorked || '').slice(0, 10);
          return d >= start && d <= end;
        });
        matchingIds = matching.map(r => r.id);
        totalCost = matching.reduce((s, r) => s + r.totalCost, 0);
      } else {
        const matching = equipData.filter(r => {
          const d = (r.dateUsed || '').slice(0, 10);
          return d >= start && d <= end;
        });
        matchingIds = matching.map(r => r.id);
        totalCost = matching.reduce((s, r) => s + r.totalCost, 0);
      }

      // Compute claim number
      const year = new Date(start).getFullYear();
      const prefix = claimForm.claimType === 'labor' ? 'FAL' : 'FAE';
      const existingCount = claimsData.filter(c => c.claimType === claimForm.claimType).length;
      const seq = String(existingCount + 1).padStart(3, '0');
      const claimNumber = `${prefix}-${year}-${seq}`;

      const payload: Record<string, unknown> = {
        [FC.claimNumber]: claimNumber,
        [FC.claimType]: claimForm.claimType,
        [FC.billingPeriodStart]: start,
        [FC.billingPeriodEnd]: end,
        [FC.status]: 'draft',
        [FC.totalCost]: totalCost,
        [FC.entryIds]: JSON.stringify(matchingIds),
        [FC.notes]: claimForm.notes,
      };
      if (incidentId) payload[FC.incidentId] = incidentId;

      await createRecord(TABLES.debrisForceAccountClaims, payload);
      notifications.show({
        title: 'Claim Created',
        message: `${claimNumber} created — ${matchingIds.length} entries included (${fmtCurrency(totalCost)})`,
        color: 'green',
      });
      setClaimModalOpen(false);
      await loadData();
    } catch (e) {
      notifications.show({ title: 'Error', message: String(e), color: 'red' });
    }
    setClaimSaving(false);
  };

  const openClaimDetail = (claim: FAClaim) => {
    setClaimDetailTarget(claim);
    setClaimDetailNotes(claim.notes);
    setClaimDetailStatus(claim.status);
  };

  const saveClaimDetail = async () => {
    if (!claimDetailTarget) return;
    setClaimDetailSaving(true);
    try {
      await updateRecord(TABLES.debrisForceAccountClaims, claimDetailTarget.id, {
        [FC.status]: claimDetailStatus,
        [FC.notes]: claimDetailNotes,
      });
      notifications.show({ title: 'Updated', message: 'Claim updated', color: 'green' });
      setClaimDetailTarget(null);
      await loadData();
    } catch (e) {
      notifications.show({ title: 'Error', message: String(e), color: 'red' });
    }
    setClaimDetailSaving(false);
  };

  const CLAIM_STATUS_COLORS: Record<ClaimStatus, string> = {
    draft: 'gray',
    submitted: 'blue',
    approved: 'green',
    paid: 'teal',
  };

  // Sort header (claims tab — uses its own sort state)
  const ClaimSortTh = ({ field, label, style: s }: { field: string; label: string; style?: React.CSSProperties }) => (
    <th
      onClick={() => toggleClaimSort(field)}
      style={{
        cursor: 'pointer', userSelect: 'none', padding: '10px 12px', fontSize: 12,
        fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em',
        color: claimSortField === field ? '#4B3FFF' : '#64748b',
        borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', ...s,
      }}
    >
      {label} {claimSortField === field ? (claimSortDir === 'desc' ? '↓' : '↑') : <span style={{ opacity: 0.25 }}>↑</span>}
    </th>
  );

  // Sort header
  const SortTh = ({ field, label, style: s }: { field: string; label: string; style?: React.CSSProperties }) => (
    <th
      onClick={() => toggleSort(field)}
      style={{
        cursor: 'pointer', userSelect: 'none', padding: '10px 12px', fontSize: 12,
        fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em',
        color: sortField === field ? '#4B3FFF' : '#64748b',
        borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap', ...s,
      }}
    >
      {label} {sortField === field ? (sortDir === 'desc' ? '↓' : '↑') : <span style={{ opacity: 0.25 }}>↑</span>}
    </th>
  );

  return (
    <Stack gap={12}>
      <Group style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Title order={3} style={{ margin: 0 }}>Force Account Tracking</Title>
        <Group gap="sm">
          {rosterLoaded && rosterPersonnel.length > 0 && (
            <Badge size="sm" color="green" variant="light">
              <IconUsers size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              {rosterPersonnel.length} roster personnel loaded
            </Badge>
          )}
          <Button variant="subtle" size="compact-sm" onClick={loadData}>
            <IconRefresh size={14} />
          </Button>
        </Group>
      </Group>

      {/* Roster not loaded alert */}
      {rosterLoaded && rosterPersonnel.length === 0 && (
        <Alert color="blue" icon={<IconAlertTriangle size={16} />} style={{ padding: '10px 16px' }}>
          <Text size="sm">
            No ERC roster data found. Import a roster from the <strong>Personnel Roster</strong> page first to enable auto-fill and bulk generate.
          </Text>
        </Alert>
      )}

      {/* Stats */}
      <SimpleGrid cols={3} style={{ gap: 12 }}>
        {[
          { label: 'Total Records', value: currentStats.count, color: '#4B3FFF', icon: <IconClock size={20} /> },
          { label: 'Total Hours', value: currentStats.totalHours.toLocaleString(), color: '#037730', icon: <IconClock size={20} /> },
          { label: 'Total Cost', value: fmtCurrency(currentStats.totalCost), color: '#d97706', icon: <IconTruck size={20} /> },
        ].map((s, i) => (
          <Paper key={i} style={{
            padding: 16, borderRadius: 12, border: '1px solid #e8eaf0',
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)', textAlign: 'center',
          }}>
            <div style={{ color: s.color, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
            <Text size="sm" style={{ color: '#64748b' }}>{s.label}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Tabs */}
      <Tabs value={tab} onChange={(v: string | null) => { setTab(v || 'labor'); setSortField(''); setClaimSortField(''); }}>
        <Tabs.List>
          <Tabs.Tab value="labor"><IconClock size={14} style={{ marginRight: 4 }} /> Labor</Tabs.Tab>
          <Tabs.Tab value="equipment"><IconTruck size={14} style={{ marginRight: 4 }} /> Equipment</Tabs.Tab>
          <Tabs.Tab value="claims"><IconCheck size={14} style={{ marginRight: 4 }} /> Claims</Tabs.Tab>
        </Tabs.List>

        {/* LABOR TAB */}
        <Tabs.Panel value="labor">
          <Group gap="sm" mt="sm" style={{ justifyContent: 'space-between' }}>
            <Group gap="sm">
              {drOptions.length > 0 && (
                <Select
                  size="xs"
                  placeholder="All disasters"
                  data={drOptions}
                  value={drFilter}
                  onChange={setDrFilter}
                  clearable
                  w={140}
                />
              )}
            </Group>
            <Group gap="sm">
              {canEdit && rosterPersonnel.length > 0 && (
                <Button size="compact-sm" variant="light" color="violet" onClick={openBulkGenerate}>
                  <IconCalendar size={14} style={{ marginRight: 4 }} /> Bulk Generate
                </Button>
              )}
              {canEdit && (
                <Button size="compact-sm" onClick={openLaborAdd}>
                  <IconPlus size={14} style={{ marginRight: 4 }} /> Add Entry
                </Button>
              )}
              <Button variant="subtle" size="compact-sm" onClick={() => exportLaborCSV(laborData)}>
                <IconDownload size={14} style={{ marginRight: 4 }} /> Export FEMA Summary
              </Button>
            </Group>
          </Group>

          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 440px)', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                <tr>
                  <SortTh field="employeeName" label="Employee" />
                  <SortTh field="employeeId" label="ID" />
                  <SortTh field="department" label="Dept" />
                  <SortTh field="classification" label="Classification" />
                  <SortTh field="dateWorked" label="Date" />
                  <SortTh field="hoursRegular" label="Reg Hrs" />
                  <SortTh field="hoursOvertime" label="OT Hrs" />
                  <SortTh field="hourlyRate" label="Rate" />
                  <SortTh field="diffTotal" label="Diff $" />
                  <SortTh field="totalCost" label="Total" />
                  <SortTh field="ercDistrict" label="ERC" />
                  <SortTh field="disasterNumber" label="DR / Cat" />
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', width: 60, fontSize: 12, fontWeight: 600, color: '#64748b' }}>Docs</th>
                  {canEdit && <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', width: 40 }} />}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={canEdit ? 14 : 13} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading...</td></tr>
                )}
                {!loading && sortedLabor.length === 0 && (
                  <tr><td colSpan={canEdit ? 14 : 13} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No labor entries</td></tr>
                )}
                {sortedLabor.map(r => {
                  const dCount = getFADocs('labor', r.id).length;
                  const diffLabels = [
                    r.nightDiff ? `N` : null,
                    r.weekendDiff ? `W` : null,
                    r.holidayDiff ? `H` : null,
                  ].filter(Boolean).join('+');
                  return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{r.employeeName}</td>
                    <td style={{ padding: '10px 12px', fontVariantNumeric: 'tabular-nums' }}>{r.employeeId || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{r.department || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 12 }}>{r.classification || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtDate(r.dateWorked)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.hoursRegular || '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.hoursOvertime || '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(r.hourlyRate)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {r.diffTotal > 0
                        ? <span style={{ color: '#9333ea' }} title={`Night: $${r.nightDiff}/hr  Weekend: $${r.weekendDiff}/hr  Holiday: $${r.holidayDiff}/hr`}>
                            {fmtCurrency(r.diffTotal)}{diffLabels ? <span style={{ fontSize: 10, marginLeft: 3, opacity: 0.7 }}>{diffLabels}</span> : null}
                          </span>
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(r.totalCost)}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {r.ercDistrict && <Badge size="sm" color="blue">{r.ercDistrict}</Badge>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <Group gap={4} wrap="nowrap">
                        {r.disasterNumber && <Badge size="xs" color="#0E7490" variant="light">{r.disasterNumber}</Badge>}
                        {r.femaCategory && <Badge size="xs" color={r.femaCategory === 'Cat A' ? '#C68700' : '#4B3FFF'} variant="light">{r.femaCategory}</Badge>}
                      </Group>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <button
                        onClick={() => setFaDocTarget({ type: 'labor', id: r.id, label: r.employeeName || `Labor #${r.id}` })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: dCount > 0 ? '#7C3AED' : '#94a3b8' }}
                      >
                        <IconPaperclip size={14} />
                        {dCount > 0 && <span style={{ fontSize: 11, fontWeight: 600 }}>{dCount}</span>}
                      </button>
                    </td>
                    {canEdit && (
                      <td style={{ padding: '10px 12px' }}>
                        <Group gap={8} wrap="nowrap">
                          <IconPencil size={14} style={{ cursor: 'pointer', color: '#4B3FFF' }} onClick={() => openLaborEdit(r)} />
                          <IconTrash size={14} style={{ cursor: 'pointer', color: '#dc2626' }} onClick={() => deleteLabor(r.id)} />
                        </Group>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Tabs.Panel>

        {/* EQUIPMENT TAB */}
        <Tabs.Panel value="equipment">
          <Group gap="sm" mt="sm" style={{ justifyContent: 'space-between' }}>
            <Group gap="sm">
              {drOptions.length > 0 && (
                <Select
                  size="xs"
                  placeholder="All disasters"
                  data={drOptions}
                  value={drFilter}
                  onChange={setDrFilter}
                  clearable
                  w={140}
                />
              )}
            </Group>
            <Group gap="sm">
              {canEdit && (
                <Button size="compact-sm" onClick={openEquipAdd}>
                  <IconPlus size={14} style={{ marginRight: 4 }} /> Add Entry
                </Button>
              )}
              <Button variant="subtle" size="compact-sm" onClick={() => exportEquipmentCSV(equipData)}>
                <IconDownload size={14} style={{ marginRight: 4 }} /> Export FEMA Summary
              </Button>
            </Group>
          </Group>

          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 440px)', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                <tr>
                  <SortTh field="fleetNumber" label="Fleet #" />
                  <SortTh field="equipmentType" label="Type" />
                  <SortTh field="description" label="Description" />
                  <SortTh field="dateUsed" label="Date" />
                  <SortTh field="hoursUsed" label="Hours" />
                  <SortTh field="hourlyRate" label="Rate" />
                  <SortTh field="totalCost" label="Total" />
                  <SortTh field="operatorName" label="Operator" />
                  <SortTh field="ercDistrict" label="ERC" />
                  <SortTh field="disasterNumber" label="DR / Cat" />
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', width: 60, fontSize: 12, fontWeight: 600, color: '#64748b' }}>Docs</th>
                  {canEdit && <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', width: 40 }} />}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={canEdit ? 12 : 11} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading...</td></tr>
                )}
                {!loading && sortedEquip.length === 0 && (
                  <tr><td colSpan={canEdit ? 12 : 11} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No equipment entries</td></tr>
                )}
                {sortedEquip.map(r => {
                  const dCount = getFADocs('equipment', r.id).length;
                  return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.fleetNumber || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{r.equipmentType || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#64748b' }}>{r.description || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtDate(r.dateUsed)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.hoursUsed || '—'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(r.hourlyRate)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(r.totalCost)}</td>
                    <td style={{ padding: '10px 12px' }}>{r.operatorName || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {r.ercDistrict && <Badge size="sm" color="blue">{r.ercDistrict}</Badge>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <Group gap={4} wrap="nowrap">
                        {r.disasterNumber && <Badge size="xs" color="#0E7490" variant="light">{r.disasterNumber}</Badge>}
                        {r.femaCategory && <Badge size="xs" color={r.femaCategory === 'Cat A' ? '#C68700' : '#4B3FFF'} variant="light">{r.femaCategory}</Badge>}
                      </Group>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <button
                        onClick={() => setFaDocTarget({ type: 'equipment', id: r.id, label: r.fleetNumber || r.description || `Equipment #${r.id}` })}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: dCount > 0 ? '#7C3AED' : '#94a3b8' }}
                      >
                        <IconPaperclip size={14} />
                        {dCount > 0 && <span style={{ fontSize: 11, fontWeight: 600 }}>{dCount}</span>}
                      </button>
                    </td>
                    {canEdit && (
                      <td style={{ padding: '10px 12px' }}>
                        <Group gap={8} wrap="nowrap">
                          <IconPencil size={14} style={{ cursor: 'pointer', color: '#4B3FFF' }} onClick={() => openEquipEdit(r)} />
                          <IconTrash size={14} style={{ cursor: 'pointer', color: '#dc2626' }} onClick={() => deleteEquip(r.id)} />
                        </Group>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Tabs.Panel>

        {/* CLAIMS TAB */}
        <Tabs.Panel value="claims">
          <Group gap="sm" mt="sm" style={{ justifyContent: 'flex-end' }}>
            {canEdit && (
              <Button size="compact-sm" onClick={openNewClaim}>
                <IconPlus size={14} style={{ marginRight: 4 }} /> New Claim
              </Button>
            )}
          </Group>

          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 440px)', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8fafc' }}>
                <tr>
                  <ClaimSortTh field="claimNumber" label="Claim #" />
                  <ClaimSortTh field="claimType" label="Type" />
                  <ClaimSortTh field="billingPeriodStart" label="Billing Period" />
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>Entries</th>
                  <ClaimSortTh field="totalCost" label="Total Cost" />
                  <ClaimSortTh field="status" label="Status" />
                  <th style={{ padding: '10px 12px', borderBottom: '2px solid #e2e8f0', width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>Loading...</td></tr>
                )}
                {!loading && sortedClaims.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No claims yet. Create a claim to group entries for FEMA submission.</td></tr>
                )}
                {sortedClaims.map(claim => (
                  <tr key={claim.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: '#4B3FFF' }}>{claim.claimNumber || `#${claim.id}`}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <Badge size="sm" color={claim.claimType === 'labor' ? 'violet' : 'orange'} variant="light">
                        {claim.claimType === 'labor' ? 'Labor' : 'Equipment'}
                      </Badge>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#64748b' }}>
                      {claim.billingPeriodStart ? `${fmtDate(claim.billingPeriodStart)} – ${fmtDate(claim.billingPeriodEnd)}` : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                      {claim.entryIds.length}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtCurrency(claim.totalCost)}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <Badge size="sm" color={CLAIM_STATUS_COLORS[claim.status]} variant="light">
                        {claim.status.charAt(0).toUpperCase() + claim.status.slice(1)}
                      </Badge>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <Button size="compact-xs" variant="light" onClick={() => openClaimDetail(claim)}>
                        View / Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tabs.Panel>
      </Tabs>

      {/* ═══ Add Entry Modal ═══ */}
      <Modal
        opened={modalOpen}
        onClose={() => { setModalOpen(false); setEditEquipTarget(null); setEditLaborTarget(null); }}
        title={modalType === 'labor' ? (editLaborTarget ? 'Edit Labor Entry' : 'Add Labor Entry') : (editEquipTarget ? 'Edit Equipment Entry' : 'Add Equipment Entry')}
        size="lg"
      >
        {modalType === 'labor' ? (
          <Stack gap={12}>
            {/* Roster person picker */}
            {rosterPersonnel.length > 0 && (
              <div ref={rosterDropdownRef} style={{ position: 'relative' }}>
                <TextInput
                  label="Search Roster"
                  placeholder="Type a name or employee ID to auto-fill..."
                  value={rosterSearch}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setRosterSearch(e.target.value);
                    setShowRosterDropdown(e.target.value.length >= 2);
                  }}
                  onFocus={() => { if (rosterSearch.length >= 2) setShowRosterDropdown(true); }}
                  leftSection={<IconSearch size={14} />}
                />
                {showRosterDropdown && filteredRoster.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'white', border: '1px solid #e2e8f0', borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 240, overflowY: 'auto',
                  }}>
                    {filteredRoster.map((p, i) => (
                      <div
                        key={`${p.employeeId || p.name}-${i}`}
                        onClick={() => selectRosterPerson(p)}
                        style={{
                          padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f0f4ff')}
                        onMouseLeave={e => (e.currentTarget.style.background = '')}
                      >
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                          <div style={{ fontSize: 11, color: '#64748b' }}>
                            {p.employeeId ? `#${p.employeeId}` : ''} {p.role ? `- ${p.role}` : ''} {p.fleetNumber ? `- Fleet ${p.fleetNumber}` : ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Badge size="xs" color={p.erc === 'ERC-1' ? 'green' : p.erc === 'ERC-2' ? 'orange' : 'violet'}>{p.erc}</Badge>
                          <Badge size="xs" color={p.shift === 'A' ? 'blue' : 'red'}>{p.shift}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {showRosterDropdown && filteredRoster.length === 0 && rosterSearch.length >= 2 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: 'white', border: '1px solid #e2e8f0', borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: '12px 14px',
                    color: '#94a3b8', fontSize: 13, textAlign: 'center',
                  }}>
                    No matching personnel found
                  </div>
                )}
              </div>
            )}

            {laborForm.employeeName && (
              <Paper style={{ padding: 10, background: '#f0f4ff', borderRadius: 8, border: '1px solid #dbeafe' }}>
                <Group style={{ justifyContent: 'space-between' }}>
                  <div>
                    <Text size="sm" style={{ fontWeight: 600 }}>{laborForm.employeeName}</Text>
                    <Text size="xs" style={{ color: '#64748b' }}>
                      {laborForm.employeeId ? `#${laborForm.employeeId}` : ''} {laborForm.classification ? `- ${laborForm.classification}` : ''}
                    </Text>
                  </div>
                  <Button variant="subtle" size="compact-xs" color="gray" onClick={() => {
                    setLaborForm(f => ({ ...f, employeeName: '', employeeId: '', department: '', classification: '', equipmentUsed: '', ercDistrict: '' }));
                  }}>Clear</Button>
                </Group>
              </Paper>
            )}

            <SimpleGrid cols={2} style={{ gap: 12 }}>
              <TextInput label="Employee Name" required value={laborForm.employeeName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, employeeName: e.target.value }))} />
              <TextInput label="Employee ID" value={laborForm.employeeId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, employeeId: e.target.value }))} />
            </SimpleGrid>
            <SimpleGrid cols={2} style={{ gap: 12 }}>
              <TextInput label="Department" value={laborForm.department}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, department: e.target.value }))} />
              <TextInput label="Classification" value={laborForm.classification}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, classification: e.target.value }))} />
            </SimpleGrid>
            <SimpleGrid cols={2} style={{ gap: 12 }}>
              <TextInput label="Equipment / Fleet #" value={laborForm.equipmentUsed}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, equipmentUsed: e.target.value }))} />
              <Select label="ERC District" data={ERC_OPTIONS} value={laborForm.ercDistrict}
                onChange={(v: string | null) => setLaborForm(f => ({ ...f, ercDistrict: v || '' }))} />
            </SimpleGrid>
            <SimpleGrid cols={2} style={{ gap: 12 }}>
              <TextInput label="Date Worked" type="date" value={(laborForm.dateWorked || '').slice(0, 10)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, dateWorked: e.currentTarget.value }))} />
              <div /> {/* spacer */}
            </SimpleGrid>
            <SimpleGrid cols={3} style={{ gap: 12 }}>
              <TextInput label="Regular Hours" type="number" value={laborForm.hoursRegular}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, hoursRegular: e.target.value }))} />
              <TextInput label="OT Hours" type="number" value={laborForm.hoursOvertime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, hoursOvertime: e.target.value }))} />
              <TextInput label="Hourly Rate" type="number" value={laborForm.hourlyRate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, hourlyRate: e.target.value }))}
                leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
            </SimpleGrid>
            <SimpleGrid cols={3} style={{ gap: 12 }}>
              <TextInput label="Night Diff ($/hr)" type="number" placeholder="0.00" value={laborForm.nightDiff}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, nightDiff: e.target.value }))}
                leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
              <TextInput label="Weekend Diff ($/hr)" type="number" placeholder="0.00" value={laborForm.weekendDiff}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, weekendDiff: e.target.value }))}
                leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
              <TextInput label="Holiday Diff ($/hr)" type="number" placeholder="0.00" value={laborForm.holidayDiff}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLaborForm(f => ({ ...f, holidayDiff: e.target.value }))}
                leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
            </SimpleGrid>
            {laborForm.hoursRegular && laborForm.hourlyRate && (
              <Paper style={{ padding: 12, background: '#f0fdf4', borderRadius: 8 }}>
                {(() => {
                  const reg = parseFloat(laborForm.hoursRegular) || 0;
                  const ot = parseFloat(laborForm.hoursOvertime) || 0;
                  const rate = parseFloat(laborForm.hourlyRate) || 0;
                  const night = parseFloat(laborForm.nightDiff) || 0;
                  const weekend = parseFloat(laborForm.weekendDiff) || 0;
                  const holiday = parseFloat(laborForm.holidayDiff) || 0;
                  const base = (reg * rate) + (ot * rate * 1.5);
                  const diffTotal = (reg + ot) * (night + weekend + holiday);
                  return (
                    <Group style={{ justifyContent: 'space-between' }}>
                      <div>
                        <Text size="xs" style={{ color: '#64748b' }}>Base labor: {fmtCurrency(base)}</Text>
                        {diffTotal > 0 && <Text size="xs" style={{ color: '#64748b' }}>Differentials: {fmtCurrency(diffTotal)}</Text>}
                      </div>
                      <Text size="sm" style={{ color: '#037730', fontWeight: 700 }}>
                        Total: {fmtCurrency(base + diffTotal)}
                      </Text>
                    </Group>
                  );
                })()}
              </Paper>
            )}
            <Textarea label="Notes" value={laborForm.notes} rows={2}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setLaborForm(f => ({ ...f, notes: e.target.value }))} />
            <Group style={{ justifyContent: 'flex-end' }} mt="sm">
              <Button variant="subtle" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={saveLabor} loading={saving}>Save Entry</Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap={12}>
            <TextInput label="Fleet #" value={equipForm.fleetNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEquipForm(f => ({ ...f, fleetNumber: e.target.value }))} />
            <Select
              label="FEMA Equipment Type (2025 Schedule)"
              placeholder="Search by name, code, or size..."
              data={FEMA_EQUIP_OPTIONS}
              value={equipFemaCostCode}
              searchable
              nothingFoundMessage="No matching equipment"
              onChange={(v: string | null) => {
                setEquipFemaCostCode(v);
                if (v) {
                  const entry = FEMA_EQUIP_BY_CODE[v];
                  if (entry) {
                    setEquipForm(f => ({
                      ...f,
                      equipmentType: `${entry.equipment}${entry.manufacturer ? ` — ${entry.manufacturer}` : ''}`,
                      description: [entry.specification, entry.notes].filter(Boolean).join('; '),
                      hourlyRate: String(entry.rate),
                    }));
                  }
                }
              }}
            />
            {equipFemaCostCode && FEMA_EQUIP_BY_CODE[equipFemaCostCode] && (
              <Paper style={{ padding: '8px 12px', background: '#eff6ff', borderRadius: 6, border: '1px solid #bfdbfe' }}>
                <Text size="xs" style={{ color: '#1d4ed8' }}>
                  FEMA Cost Code <strong>{equipFemaCostCode}</strong> · $<strong>{FEMA_EQUIP_BY_CODE[equipFemaCostCode].rate.toFixed(2)}</strong>/{FEMA_EQUIP_BY_CODE[equipFemaCostCode].unit} — rate auto-filled, override below if needed
                </Text>
              </Paper>
            )}
            <TextInput label="Description / Specification" value={equipForm.description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEquipForm(f => ({ ...f, description: e.target.value }))}
              error={equipForm.description.trim().length > 0 && equipForm.description.trim().length < 6 ? 'Minimum 6 characters' : undefined} />
            <SimpleGrid cols={2} style={{ gap: 12 }}>
              <TextInput label="Date Used" type="date" value={(equipForm.dateUsed || '').slice(0, 10)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEquipForm(f => ({ ...f, dateUsed: e.currentTarget.value }))} />
              <Select label="ERC District" data={ERC_OPTIONS} value={equipForm.ercDistrict}
                onChange={(v: string | null) => setEquipForm(f => ({ ...f, ercDistrict: v || '' }))} />
            </SimpleGrid>
            <SimpleGrid cols={2} style={{ gap: 12 }}>
              <TextInput label="Hours Used" type="number" value={equipForm.hoursUsed}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEquipForm(f => ({ ...f, hoursUsed: e.target.value }))} />
              <TextInput label="Hourly Rate" type="number" value={equipForm.hourlyRate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEquipForm(f => ({ ...f, hourlyRate: e.target.value }))}
                leftSection={<span style={{ color: '#64748b', fontSize: 13 }}>$</span>} />
            </SimpleGrid>
            {equipForm.hoursUsed && equipForm.hourlyRate && (
              <Paper style={{ padding: 12, background: '#f0fdf4', borderRadius: 8, textAlign: 'center' }}>
                <Text size="sm" style={{ color: '#037730' }}>
                  Calculated Total: {fmtCurrency((parseFloat(equipForm.hoursUsed) || 0) * (parseFloat(equipForm.hourlyRate) || 0))}
                </Text>
              </Paper>
            )}
            <TextInput label="Operator Name" value={equipForm.operatorName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEquipForm(f => ({ ...f, operatorName: e.target.value }))} />
            <Textarea label="Notes" value={equipForm.notes} rows={2}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEquipForm(f => ({ ...f, notes: e.target.value }))} />
            <Group style={{ justifyContent: 'flex-end' }} mt="sm">
              <Button variant="subtle" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={saveEquipment} loading={saving}>Save Entry</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ═══ FA Supporting Docs Modal ═══ */}
      <Modal
        opened={!!faDocTarget}
        onClose={() => setFaDocTarget(null)}
        title={`Supporting Docs — ${faDocTarget?.label ?? ''}`}
        size="md"
      >
        {faDocTarget && (() => {
          const attached = getFADocs(faDocTarget.type, faDocTarget.id);
          return (
            <Stack gap={12}>
              <Text size="xs" c="dimmed">
                Files attached here are stored in <strong>Documentation Archive → FA Support Docs</strong> and linked to this record.
              </Text>

              {attached.length === 0 ? (
                <Paper style={{ padding: 20, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 8 }}>
                  <IconPaperclip size={24} style={{ marginBottom: 8, opacity: 0.4 }} />
                  <Text size="sm">No documents attached yet</Text>
                </Paper>
              ) : (
                <Stack gap={6}>
                  {attached.map((doc: any) => {
                    const hasFile = (doc.notes || '').includes('|||DOCID|||');
                    return (
                      <Paper key={doc.id} style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8 }}>
                        <Group style={{ justifyContent: 'space-between' }}>
                          <Group gap={8}>
                            <IconPaperclip size={14} color="#7C3AED" />
                            <div>
                              <Text size="sm" fw={500}>{doc.title || 'Untitled'}</Text>
                              {doc.uploadedBy && <Text size="xs" c="dimmed">{doc.uploadedBy}</Text>}
                            </div>
                          </Group>
                          {hasFile && (
                            <Button
                              variant="subtle"
                              size="compact-xs"
                              onClick={() => handleFADocOpen(doc)}
                            >
                              <IconExternalLink size={13} style={{ marginRight: 4 }} /> Open
                            </Button>
                          )}
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              )}

              <input
                ref={faFileRef}
                type="file"
                style={{ display: 'none' }}
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  await handleFADocUpload(file);
                  e.target.value = '';
                }}
              />
              <Group style={{ justifyContent: 'flex-end' }} mt="xs">
                <Button variant="subtle" onClick={() => setFaDocTarget(null)}>Close</Button>
                <Button
                  onClick={() => faFileRef.current?.click()}
                  loading={docUploading}
                  leftSection={<IconUpload size={14} />}
                >
                  Attach File
                </Button>
              </Group>
            </Stack>
          );
        })()}
      </Modal>

      {/* ═══ Bulk Generate Modal ═══ */}
      <Modal
        opened={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk Generate Labor Records"
        size="xl"
      >
        <Stack gap={16}>
          <Text size="sm" style={{ color: '#64748b' }}>
            Select personnel from the ERC roster, set a date range and shift hours. Each person's hourly rate
            is pre-filled from the roster — edit any individual rate below before generating.
          </Text>

          {/* Date range + hours */}
          <Paper style={{ padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e8eaf0' }}>
            <Text size="sm" style={{ fontWeight: 600, marginBottom: 8 }}>Schedule</Text>
            <SimpleGrid cols={2} style={{ gap: 12 }}>
              <TextInput label="Start Date" type="date" value={bulkDateStart}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBulkDateStart(e.target.value)} />
              <TextInput label="End Date" type="date" value={bulkDateEnd}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBulkDateEnd(e.target.value)} />
            </SimpleGrid>
            <SimpleGrid cols={2} style={{ gap: 12, marginTop: 12 }}>
              <TextInput label="Regular Hours / Day" type="number" value={bulkHoursReg}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBulkHoursReg(e.target.value)} />
              <TextInput label="OT Hours / Day" type="number" value={bulkHoursOT}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBulkHoursOT(e.target.value)} />
            </SimpleGrid>
            {bulkDateStart && bulkDateEnd && (
              <Text size="xs" style={{ color: '#64748b', marginTop: 8 }}>
                {dateRange(bulkDateStart, bulkDateEnd).length} days selected
              </Text>
            )}
          </Paper>

          {/* Personnel selection */}
          <Paper style={{ padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e8eaf0' }}>
            <Group style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <Text size="sm" style={{ fontWeight: 600 }}>
                Personnel ({bulkSelectedIds.size} of {bulkEligiblePersonnel.length} selected)
              </Text>
              <Group gap="xs">
                <Button variant="subtle" size="compact-xs" onClick={selectAllBulk}>Select All</Button>
                <Button variant="subtle" size="compact-xs" color="gray" onClick={deselectAllBulk}>Deselect All</Button>
              </Group>
            </Group>

            {/* Filters */}
            <Group gap="sm" style={{ marginBottom: 12 }}>
              <TextInput
                placeholder="Search..."
                value={bulkFilter}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBulkFilter(e.target.value)}
                style={{ flex: 1, maxWidth: 200 }}
                leftSection={<IconSearch size={14} />}
                size="xs"
              />
              <Select
                data={[{ value: '', label: 'All Shifts' }, { value: 'A', label: 'A-Shift' }, { value: 'B', label: 'B-Shift' }]}
                value={bulkShiftFilter}
                onChange={(v: string | null) => setBulkShiftFilter(v || '')}
                style={{ width: 110 }}
                size="xs"
                placeholder="Shift"
              />
              <Select
                data={[{ value: '', label: 'All Roles' }, ...bulkRoles.map(r => ({ value: r, label: r }))]}
                value={bulkRoleFilter}
                onChange={(v: string | null) => setBulkRoleFilter(v || '')}
                style={{ width: 160 }}
                size="xs"
                placeholder="Role"
              />
            </Group>

            {/* Personnel list */}
            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f1f5f9' }}>
                  <tr>
                    <th style={{ padding: '6px 10px', textAlign: 'left', width: 30 }}></th>
                    <th style={{ padding: '6px 10px', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left' }}>ID</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left' }}>Role</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left' }}>Shift</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left' }}>ERC</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left' }}>Fleet #</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left' }}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkEligiblePersonnel.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>
                      No debris-relevant personnel found. Import a roster first.
                    </td></tr>
                  )}
                  {bulkEligiblePersonnel.map((p, idx) => {
                    const key = `${p.employeeId || p.name}-${p.shift}`;
                    const checked = bulkSelectedIds.has(key);
                    return (
                      <tr
                        key={`${key}-${idx}`}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          background: checked ? '#f0f4ff' : '',
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleBulkPerson(key)}
                      >
                        <td style={{ padding: '6px 10px' }}>
                          <Checkbox checked={checked} onChange={() => toggleBulkPerson(key)} />
                        </td>
                        <td style={{ padding: '6px 10px', fontWeight: 600 }}>{p.name}</td>
                        <td style={{ padding: '6px 10px', fontVariantNumeric: 'tabular-nums' }}>{p.employeeId || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>{p.role || '—'}</td>
                        <td style={{ padding: '6px 10px' }}>
                          <Badge size="xs" color={p.shift === 'A' ? 'blue' : 'red'}>{p.shift}</Badge>
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <Badge size="xs" color={p.erc === 'ERC-1' ? 'green' : p.erc === 'ERC-2' ? 'orange' : 'violet'}>{p.erc}</Badge>
                        </td>
                        <td style={{ padding: '6px 10px', color: '#64748b' }}>{p.fleetNumber || '—'}</td>
                        <td style={{ padding: '6px 10px', color: p.hourlyRate ? '#1e293b' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>
                          {p.hourlyRate ? `$${p.hourlyRate.toFixed(2)}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Paper>

          {/* Preview + confirm */}
          {bulkPreviewCount > 0 && (
            <Alert color="blue" icon={<IconCheck size={16} />}>
              <Text size="sm">
                This will create <strong>{bulkPreviewCount.toLocaleString()} labor records</strong> ({bulkSelectedIds.size} people × {dateRange(bulkDateStart, bulkDateEnd).length} days).
                {(() => {
                  const reg = parseFloat(bulkHoursReg) || 0;
                  const ot = parseFloat(bulkHoursOT) || 0;
                  const days = dateRange(bulkDateStart, bulkDateEnd).length;
                  const selectedPeople = bulkEligiblePersonnel.filter(p => bulkSelectedIds.has(`${p.employeeId || p.name}-${p.shift}`));
                  const est = selectedPeople.reduce((s, p) => {
                    const rate = p.hourlyRate || 0;
                    return s + days * ((reg * rate) + (ot * rate * 1.5));
                  }, 0);
                  return est > 0 ? ` Estimated total: ${fmtCurrency(est)}` : ' Rates will be pulled from the roster for each person.';
                })()}
              </Text>
            </Alert>
          )}

          <Group style={{ justifyContent: 'flex-end' }}>
            <Button variant="subtle" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button
              onClick={saveBulkGenerate}
              loading={bulkSaving}
              disabled={bulkSelectedIds.size === 0 || !bulkDateStart || !bulkDateEnd}
            >
              <IconCalendar size={14} style={{ marginRight: 4 }} />
              Generate {bulkPreviewCount > 0 ? `${bulkPreviewCount.toLocaleString()} Records` : 'Records'}
            </Button>
          </Group>
        </Stack>
      </Modal>
      {/* ═══ New Claim Modal ═══ */}
      <Modal
        opened={claimModalOpen}
        onClose={() => setClaimModalOpen(false)}
        title="New Billing Period Claim"
        size="md"
      >
        <Stack gap={12}>
          <Text size="sm" style={{ color: '#64748b' }}>
            Create a claim to group Force Account entries for a billing period. Matching entries will be automatically linked based on type and date range.
          </Text>
          <Select
            label="Claim Type"
            required
            data={[
              { value: 'labor', label: 'Labor' },
              { value: 'equipment', label: 'Equipment' },
            ]}
            value={claimForm.claimType}
            onChange={(v: string | null) => setClaimForm(f => ({ ...f, claimType: (v as 'labor' | 'equipment') || 'labor' }))}
          />
          <SimpleGrid cols={2} style={{ gap: 12 }}>
            <TextInput
              label="Billing Period Start"
              type="date"
              required
              value={claimForm.billingPeriodStart}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClaimForm(f => ({ ...f, billingPeriodStart: e.target.value }))}
            />
            <TextInput
              label="Billing Period End"
              type="date"
              required
              value={claimForm.billingPeriodEnd}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClaimForm(f => ({ ...f, billingPeriodEnd: e.target.value }))}
            />
          </SimpleGrid>
          {claimForm.billingPeriodStart && claimForm.billingPeriodEnd && claimForm.billingPeriodStart <= claimForm.billingPeriodEnd && (
            <Paper style={{ padding: '10px 14px', background: '#f0f4ff', borderRadius: 8, border: '1px solid #dbeafe' }}>
              <Text size="xs" style={{ color: '#4B3FFF' }}>
                {(() => {
                  const entries = claimForm.claimType === 'labor'
                    ? laborData.filter(r => { const d = (r.dateWorked || '').slice(0, 10); return d >= claimForm.billingPeriodStart && d <= claimForm.billingPeriodEnd; })
                    : equipData.filter(r => { const d = (r.dateUsed || '').slice(0, 10); return d >= claimForm.billingPeriodStart && d <= claimForm.billingPeriodEnd; });
                  const total = entries.reduce((s, r) => s + r.totalCost, 0);
                  return `${entries.length} ${claimForm.claimType} entries match this period — estimated total: ${fmtCurrency(total)}`;
                })()}
              </Text>
            </Paper>
          )}
          <Textarea
            label="Notes (optional)"
            value={claimForm.notes}
            rows={3}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setClaimForm(f => ({ ...f, notes: e.target.value }))}
          />
          <Group style={{ justifyContent: 'flex-end' }} mt="sm">
            <Button variant="subtle" onClick={() => setClaimModalOpen(false)}>Cancel</Button>
            <Button onClick={saveNewClaim} loading={claimSaving}>Create Claim</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ═══ Claim Detail Modal ═══ */}
      <Modal
        opened={!!claimDetailTarget}
        onClose={() => setClaimDetailTarget(null)}
        title={claimDetailTarget ? `Claim ${claimDetailTarget.claimNumber || `#${claimDetailTarget.id}`}` : 'Claim Detail'}
        size="xl"
      >
        {claimDetailTarget && (() => {
          const claim = claimDetailTarget;
          const entryIdSet = new Set(claim.entryIds);
          const laborEntries = claim.claimType === 'labor'
            ? laborData.filter(r => entryIdSet.has(r.id))
            : [];
          const equipEntries = claim.claimType === 'equipment'
            ? equipData.filter(r => entryIdSet.has(r.id))
            : [];

          return (
            <Stack gap={16}>
              {/* Metadata */}
              <SimpleGrid cols={4} style={{ gap: 12 }}>
                {[
                  { label: 'Type', value: claim.claimType === 'labor' ? 'Labor' : 'Equipment' },
                  { label: 'Period', value: `${fmtDate(claim.billingPeriodStart)} – ${fmtDate(claim.billingPeriodEnd)}` },
                  { label: 'Entries', value: String(claim.entryIds.length) },
                  { label: 'Total Cost', value: fmtCurrency(claim.totalCost) },
                ].map((item, i) => (
                  <Paper key={i} style={{ padding: '10px 14px', border: '1px solid #e8eaf0', borderRadius: 8, textAlign: 'center' }}>
                    <Text size="xs" style={{ color: '#64748b', marginBottom: 4 }}>{item.label}</Text>
                    <Text size="sm" style={{ fontWeight: 700 }}>{item.value}</Text>
                  </Paper>
                ))}
              </SimpleGrid>

              {/* Entry table */}
              <div>
                <Text size="sm" style={{ fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>Included Entries</Text>
                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 300, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  {claim.claimType === 'labor' ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                        <tr>
                          {['Employee Name', 'Date', 'Reg Hrs', 'OT Hrs', 'Total Cost', 'ERC'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {laborEntries.length === 0 && (
                          <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>No matching entries found in loaded data</td></tr>
                        )}
                        {laborEntries.map(r => (
                          <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600 }}>{r.employeeName}</td>
                            <td style={{ padding: '8px 12px' }}>{fmtDate(r.dateWorked)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.hoursRegular || '—'}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.hoursOvertime || '—'}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(r.totalCost)}</td>
                            <td style={{ padding: '8px 12px' }}>{r.ercDistrict ? <Badge size="xs" color="blue">{r.ercDistrict}</Badge> : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                        <tr>
                          {['Fleet #', 'Type', 'Date', 'Hours', 'Rate', 'Total Cost'].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {equipEntries.length === 0 && (
                          <tr><td colSpan={6} style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>No matching entries found in loaded data</td></tr>
                        )}
                        {equipEntries.map(r => (
                          <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 12px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.fleetNumber || '—'}</td>
                            <td style={{ padding: '8px 12px' }}>{r.equipmentType || '—'}</td>
                            <td style={{ padding: '8px 12px' }}>{fmtDate(r.dateUsed)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.hoursUsed || '—'}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(r.hourlyRate)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(r.totalCost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Status + Notes editable */}
              <SimpleGrid cols={2} style={{ gap: 12 }}>
                <Select
                  label="Status"
                  data={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'submitted', label: 'Submitted' },
                    { value: 'approved', label: 'Approved' },
                    { value: 'paid', label: 'Paid' },
                  ]}
                  value={claimDetailStatus}
                  onChange={(v: string | null) => setClaimDetailStatus((v as ClaimStatus) || 'draft')}
                />
                <div /> {/* spacer */}
              </SimpleGrid>
              <Textarea
                label="Notes"
                value={claimDetailNotes}
                rows={3}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setClaimDetailNotes(e.target.value)}
              />

              <Group style={{ justifyContent: 'flex-end' }} mt="sm">
                <Button variant="subtle" onClick={() => setClaimDetailTarget(null)}>Close</Button>
                <Button onClick={saveClaimDetail} loading={claimDetailSaving}>Save Changes</Button>
              </Group>
            </Stack>
          );
        })()}
      </Modal>
    </Stack>
  );
}
